// src/Components/Scene3d.js

// Import our dependancies
import React, { Component } from "react";
import { TweenMax, Power2 } from "gsap";
// Destructuring really helps clean up babylon projects
import {
  Scene,
  Engine,
  StandardMaterial,
  ArcRotateCamera,
  Texture,
  Vector3,
  ShadowGenerator,
  DefaultRenderingPipeline,
  SceneLoader,
  Color3,
  Color4,
  Tools,
  MeshBuilder,
  DirectionalLight,
  CubeTexture,
  MirrorTexture,
  Plane,
  ActionManager,
  ExecuteCodeAction,
  PointerEventTypes,
  DefaultLoadingScreen,
  AnimationGroup,
  Animation,
  VideoTexture,
  SceneOptimizerOptions,
  HardwareScalingOptimization,
  SceneOptimizer,
  Quaternion,
  Axis,
} from "babylonjs";
import "babylonjs-loaders";
import { ShadowOnlyMaterial } from "babylonjs-materials";
import { GLTF2Export } from "babylonjs-serializers";

const queryParameters = new URLSearchParams(window.location.search);
const sku = queryParameters.get("sku");

const models = {
  Camera: {
    id: 0,
    cameraValues: {
      targetX: 0,
      targetY: 0.42,
      targetZ: 0,
      alpha: 1.57,
      beta: 1.57,
      radius: 2,
    },
    mobileCameraValues: {
      targetX: 0,
      targetY: 0.42,
      targetZ: 0,
      alpha: 1.57,
      beta: 1.57,
      radius: 2.6,
    },
  },
  PC: {
    id: 1,
    cameraValues: {
      targetX: -0.02,
      targetY: 0.35,
      targetZ: 0,
      alpha: 1.57,
      beta: 1.57,
      radius: 2.1,
    },
    mobileCameraValues: {
      targetX: -0.02,
      targetY: 0.35,
      targetZ: 0,
      alpha: 1.57,
      beta: 1.57,
      radius: 2.6,
    },
  },

  Watch: {
    id: 2,
    cameraValues: {
      targetX: 0.02,
      targetY: 0.5,
      targetZ: 0,
      alpha: 1.55,
      beta: 1.45,
      radius: 2.8,
    },
    mobileCameraValues: {
      targetX: 0.02,
      targetY: 0.5,
      targetZ: 0,
      alpha: 1.55,
      beta: 1.45,
      radius: 3,
    },
  },
  VRSet: {
    id: 3,
    cameraValues: {
      targetX: 0,
      targetY: 0.14,
      targetZ: 0,
      alpha: 1.56,
      beta: 1.3,
      radius: 1.9,
    },
    mobileCameraValues: {
      targetX: 0,
      targetY: 0.14,
      targetZ: 0,
      alpha: 1.56,
      beta: 1.3,
      radius: 2.5,
    },
  },
  TypeWriter: {
    id: 4,
    cameraValues: {
      targetX: 0,
      targetY: 0.167,
      targetZ: 0.015,
      alpha: 3.12,
      beta: 1.2,
      radius: 2.1,
    },
    mobileCameraValues: {
      targetX: 0,
      targetY: 0.167,
      targetZ: 0.015,
      alpha: 3.12,
      beta: 1.2,
      radius: 2.6,
    },
  },
  Screen: {
    id: 5,
    cameraValues: {
      targetX: 0,
      targetY: 0.42,
      targetZ: 0,
      alpha: 1.57,
      beta: 1.57,
      radius: 2.2,
    },
    mobileCameraValues: {
      targetX: 0,
      targetY: 0.42,
      targetZ: 0,
      alpha: 1.57,
      beta: 1.57,
      radius: 3,
    },
  },
};

// Here we extend Reacts component class
class Scene3d extends Component {
  // Makes the canvas behave responsively
  onResizeWindow = () => {
    if (this.engine) {
      this.engine.resize();
    }
  };

  // Sets up our canvas tag for webGL scene
  setEngine = () => {
    this.engine = new Engine(this.stage, true, {
      antialias: true,
      stencil: true,
      preserveDrawingBuffer: true,
    });
    if (window.devicePixelRatio < 2) {
      this.engine.setHardwareScalingLevel(1 / 2);
    } else {
      this.engine.setHardwareScalingLevel(1 / window.devicePixelRatio);
    }
    // this.engine.setHardwareScalingLevel(1 / window.devicePixelRatio);

    // var options = new SceneOptimizerOptions(60, 1000);
    // options.addOptimization(new HardwareScalingOptimization(0, 1));
    // Optimizer
    // var optimizer = new SceneOptimizer(this.scene, options);

    this.stage.style.width = "100%";
    this.stage.style.height = "100%";
    this.stage.style.outline = "none";
    this.engine.resize();
    // var available = ['-astc.ktx', '-dxt.ktx', '-pvrtc.ktx', '-etc1.ktx', '-etc2.ktx'];
    // this.engine.setTextureFormatToUse(available);
  };

  // Creates the scene graph
  setScene = () => {
    this.scene = new Scene(this.engine);
    this.scene.clearColor = new Color4(0, 0, 0, 0);
    this.scene.autoClear = true;
    this.scene.autoClearDepthAndStencil = true;
    if (sku === "debug") {
      this.scene.debugLayer.show();
    } else {
      // this.scene.debugLayer.show();
    }
  };

  setCamera = () => {
    let cameraValues = models["Camera"].cameraValues;
    let mobileCameraValues = models["Camera"].mobileCameraValues;
    console.log(cameraValues);
    console.log(mobileCameraValues);

    if (window.innerWidth < 470) {
      this.camera = new ArcRotateCamera(
        "Camera",
        mobileCameraValues.alpha,
        mobileCameraValues.beta,
        mobileCameraValues.radius,
        new Vector3(
          mobileCameraValues.targetX,
          mobileCameraValues.targetY,
          mobileCameraValues.targetZ
        ),
        this.scene
      );
      this.camera.attachControl(this.stage, true);

      this.camera.lowerRadiusLimit = 1;
      this.camera.upperRadiusLimit = 10;
      this.camera.lowerBetaLimit = 0;
      this.camera.upperBetaLimit = 1.5;
      this.camera.panningSensibility = 0;
      this.camera.wheelPrecision = 0;
      this.camera.pinchPrecision = 0;
      this.camera.minZ = 0.001;
      this.camera.inputs.remove(this.camera.inputs.attached.mousewheel);
      this.camera.inputs.attached.pointers.pinchZoom = false;
      this.camera.inputs.attached.pointers.multiTouchPanAndZoom = false;
    } else {
      this.camera = new ArcRotateCamera(
        "Camera",
        cameraValues.alpha,
        cameraValues.beta,
        cameraValues.radius,
        new Vector3(
          cameraValues.targetX,
          cameraValues.targetY,
          cameraValues.targetZ
        ),
        this.scene
      );
      this.camera.attachControl(this.stage, true);

      this.camera.lowerRadiusLimit = 1;
      this.camera.upperRadiusLimit = 10;
      this.camera.lowerBetaLimit = 0;
      this.camera.upperBetaLimit = 1.5;
      this.camera.panningSensibility = 0;
      this.camera.wheelPrecision = 0;
      this.camera.pinchPrecision = 0;
      this.camera.minZ = 0.001;
      this.camera.inputs.remove(this.camera.inputs.attached.mousewheel);
      this.camera.pinchToPan = false;
      this.camera.inputs.attached.pointers.pinchZoom = false;

      // var pipeline = new DefaultRenderingPipeline(
      //   "defaultPipeline",
      //   true,
      //   this.scene,
      //   [this.camera]
      // );
      // pipeline.fxaaEnabled = true;
      // pipeline.samples = 64;
      // pipeline.bloomEnabled = true;
      // // pipeline.bloomThreshold = 0.7;
      // // pipeline.bloomWeight = 0.2;
      // // // pipeline.bloomKernel = 80;
      // // // pipeline.bloomScale = 0.5;
      // // pipeline.glowLayerEnabled = true;
      // // pipeline.glowLayer.blurKernelSize = 16;
      // // pipeline.glowLayer.intensity = 1;
      // pipeline.imageProcessingEnabled = false;
    }
  };

  constructor(props) {
    super(props);
    // We bind our event to keep the proper "this" context.
    this.loadModels = this.loadModels.bind(this);

    this.animationEnded = React.createRef(false);
    this.rotationSpeed = React.createRef();
  }

  loadModels = async () => {
    DefaultLoadingScreen.prototype.displayLoadingUI = function () {
      if (this._loadingDiv) {
        // Do not add a loading screen if there is already one
        return;
      }
      this._loadingDiv = document.createElement("div");
      this._loadingDiv.id = "babylonjsLoadingDiv";
      this._loadingDiv.style.opacity = "1";
      this._loadingDiv.style.transition = "opacity 1s ease";
      this._loadingDiv.style.pointerEvents = "none";
      this._loadingDiv.style.backgroundColor = "transparent";
      this._loadingDiv.style.border = "1vh solid #f3f3f3";
      this._loadingDiv.style.borderTop = "1vh solid #3E67EC";
      this._loadingDiv.style.borderRadius = "50%";
      this._loadingDiv.style.position = "absolute";
      // this._loadingDiv.style.display = "block";
      // this._loadingDiv.style.alignItems = "center";
      // this._loadingDiv.style.justifyContent = "center";
      this._loadingDiv.style.top = "calc((100vh - 100px)/2)";
      // this._loadingDiv.style.bottom = "45vh";
      this._loadingDiv.style.left = "calc((100vw - 100px)/2)";
      // this._loadingDiv.style.right = "47vw";
      this._loadingDiv.style.width = "100px";
      this._loadingDiv.style.height = "100px";
      this._loadingDiv.style.animation = "spin 1s linear infinite";
      // Generating keyframes
      var style = document.createElement("style");
      style.type = "text/css";
      var keyFrames =
        "@-webkit-keyframes spin { 0% { -webkit-transform: rotate(0deg); }\n 100% { -webkit-transform: rotate(360deg); }\n }";
      style.innerHTML = keyFrames;
      document.getElementsByTagName("head")[0].appendChild(style);
      // this._resizeLoadingUI();
      // window.addEventListener("resize", this._resizeLoadingUI);
      this._loadingDiv.style.backgroundColor = this._loadingDivBackgroundColor;
      document.body.appendChild(this._loadingDiv);
      this._loadingDiv.style.opacity = "1";
      this._loadingDiv.style.transition = "opacity 1s ease";
      this._loadingDiv.style.pointerEvents = "none";
      this._loadingDiv.style.backgroundColor = "transparent";
      this._loadingDiv.style.border = "1vh solid #f3f3f3";
      this._loadingDiv.style.borderTop = "1vh solid #3E67EC";
      this._loadingDiv.style.borderRadius = "50%";
      this._loadingDiv.style.position = "absolute";
      // this._loadingDiv.style.display = "block";
      // this._loadingDiv.style.alignItems = "center";
      // this._loadingDiv.style.justifyContent = "center";
      this._loadingDiv.style.top = "calc((100vh - 100px)/2)";
      // this._loadingDiv.style.bottom = "45vh";
      this._loadingDiv.style.left = "calc((100vw - 100px)/2)";
      // this._loadingDiv.style.right = "47vw";
      this._loadingDiv.style.width = "100px";
      this._loadingDiv.style.height = "100px";
      this._loadingDiv.style.animation = "spin 1s linear infinite";
    };

    let HDR = new CubeTexture.CreateFromPrefilteredData(
      "./hdr/studio.env",
      this.scene
    );
    this.scene.environmentTexture = HDR;

    // var rotatePlane;
    // var rotatePlaneMaterial;
    // var touchPlane;
    // var touchPlane2;
    // var touchPlane3;
    // var touchPlaneMaterial;

    await SceneLoader.AppendAsync(
      "./Models/",
      "Cam-compressed.glb",
      this.scene
    );
    let Cam = this.scene.getMeshByName("__root__");
    Cam.name = "Camera";
    Cam.setEnabled(false);
    let CamAnimation = this.scene.getAnimationGroupByName("All Animations");
    CamAnimation.name = "CameraAnimation";
    CamAnimation.stop();

    await SceneLoader.AppendAsync("./Models/", "PC-compressed.glb", this.scene);
    let PC = this.scene.getMeshByName("__root__");
    PC.name = "PC";
    PC.setEnabled(false);

    let PCAnimation = this.scene.getAnimationGroupByName("All Animations");
    PCAnimation.name = "PCAnimation";
    PCAnimation.stop();

    await SceneLoader.AppendAsync(
      "./Models/",
      "Screen-compressed.glb",
      this.scene
    );
    let Screen = this.scene.getMeshByName("__root__");
    Screen.name = "Screen";
    Screen.setEnabled(false);

    let ScreenAnimation = this.scene.getAnimationGroupByName("All Animations");
    ScreenAnimation.name = "ScreenAnimation";
    ScreenAnimation.stop();

    await SceneLoader.AppendAsync(
      "./Models/",
      "TypeWriter-compressed.glb",
      this.scene
    );
    let TypeWriter = this.scene.getMeshByName("__root__");
    TypeWriter.name = "TypeWriter";
    TypeWriter.setEnabled(false);

    let TypeWriterAnimation =
      this.scene.getAnimationGroupByName("All Animations");
    TypeWriterAnimation.name = "TypeWriterAnimation";
    TypeWriterAnimation.stop();

    await SceneLoader.AppendAsync(
      "./Models/",
      "VRSet-compressed.glb",
      this.scene
    );
    let VRSet = this.scene.getMeshByName("__root__");
    VRSet.name = "VRSet";
    VRSet.setEnabled(false);

    let VRSetAnimation = this.scene.getAnimationGroupByName("All Animations");
    VRSetAnimation.name = "VRSetAnimation";
    VRSetAnimation.stop();

    await SceneLoader.AppendAsync(
      "./Models/",
      "Watch-compressed.glb",
      this.scene
    );
    let Watch = this.scene.getMeshByName("__root__");
    Watch.name = "Watch";
    Watch.setEnabled(false);

    let WatchAnimation = this.scene.getAnimationGroupByName("All Animations");
    WatchAnimation.name = "WatchAnimation";
    WatchAnimation.stop();

    this.scene.materials.map((material) => {
      material.transparencyMode = 0;
    });

    // var light = new DirectionalLight(
    //   "dirLight",
    //   new Vector3(0, -1, 0),
    //   this.scene
    // );
    // light.position = new Vector3(0.001, 1, -0.003);
    // // light.setDirectionToTarget(new Vector3(0, 0, 0));
    // light.diffuseColor = new Color3(90, 90, 90);
    // light.shadowMinZ = 1.0;
    // light.shadowMaxZ = -1.0;
    // light.intensity = 1;

    // let mirror = new MirrorTexture("mirror", 512, this.scene);
    // mirror.mirrorPlane = new Plane(0, -1, 0, -0.01);
    // let disc = MeshBuilder.CreateDisc("disc", { radius: 10 }, this.scene);
    // disc.position = new Vector3(-0.04, -0.0015, -0.064);
    // disc.rotation = new Vector3(Math.PI / 2, 0, 0);
    // let discMaterial = new ShadowOnlyMaterial("shadowOnly", this.scene);
    // discMaterial.backFaceCulling = false;
    // discMaterial.alpha = 1;
    // disc.material = discMaterial;
    // disc.receiveShadows = true;

    // var shadowGenerator = new ShadowGenerator(1024, light);
    // shadowGenerator.useBlurExponentialShadowMap = true;
    // shadowGenerator.addShadowCaster(Model);
    // shadowGenerator.bias = -0.1;
    // shadowGenerator.normalBias = -0.005;
    // shadowGenerator.getShadowMap().renderList.push(Model);
    // shadowGenerator.frustumEdgeFalloff = 1.0;
    // shadowGenerator.darkness = 0.58;
    // shadowGenerator.blurScale = 4;
    // shadowGenerator.useKernelBlur = true;
    // shadowGenerator.blurKernel = 64;
    let that = this;
    this.scene.executeWhenReady(async function () {
      Cam.setEnabled(true);
      that.props.setisLoadingVisible(false);
      setTimeout(() => {
        CamAnimation.play(true);
      }, 200);
    });

    // var speed = -0.002;
    // this.rotationSpeed.current = -0.002;
    // var myfunc;
    // function Func() {
    //   that.rotationSpeed.current = -0.002;
    // }
    // var that = this;
    // this.scene.onPointerObservable.add((pointerInfo) => {
    //   switch (pointerInfo.type) {
    //     case PointerEventTypes.POINTERDOWN:
    //       clearTimeout(myfunc);
    //       this.rotationSpeed.current = 0;
    //       break;
    //     case PointerEventTypes.POINTERUP:
    //       myfunc = setTimeout(Func, 1500);
    //       break;
    //   }
    // });

    // window.onpointerdown = (evt) => {
    //   if (this.scene.getMeshByName("touchPlane")) {
    //     this.scene.getMeshByName("touchPlane").setEnabled(false);
    //   }
    //   if (this.scene.getMeshByName("touchPlane2")) {
    //     this.scene.getMeshByName("touchPlane2").setEnabled(false);
    //   }
    //   if (this.scene.getMeshByName("touchPlane3")) {
    //     this.scene.getMeshByName("touchPlane3").setEnabled(false);
    //   }
    //   this.scene.getMeshByName("rotatePlane").setEnabled(false);
    // };
    // window.onwheel = (evt) => {

    //   this.scene.getMeshByName("rotatePlane").setEnabled(false);
    // };

    this.engine.runRenderLoop(() => {
      this.scene.render();
    });
  };

  animateModelQuaternionRotation = (
    mesh,
    fromRotationY,
    toRotationY,
    duration = 1
  ) => {
    // Convert Euler Y-rotation (angles in radians) to quaternions
    let fromQuaternion = Quaternion.RotationAxis(Axis.Y, fromRotationY);
    let toQuaternion = Quaternion.RotationAxis(Axis.Y, toRotationY);

    // Create the quaternion animation
    let animation = new Animation(
      "quaternionRotationAnimation",
      "rotationQuaternion", // We are animating the quaternion
      30, // Frame rate (30 frames per second)
      Animation.ANIMATIONTYPE_QUATERNION, // We're animating a quaternion
      Animation.ANIMATIONLOOPMODE_CONSTANT // The animation will not loop
    );

    // Define the keyframes (from starting to ending quaternions)
    let keys = [];
    keys.push({ frame: 0, value: fromQuaternion }); // Starting quaternion
    keys.push({ frame: duration * 30, value: toQuaternion }); // Ending quaternion

    // Set the animation keyframes
    animation.setKeys(keys);
    mesh.animations = []; // Clear any previous animations on this mesh
    mesh.animations.push(animation);

    // Start the animation
    this.scene.beginAnimation(mesh, 0, duration * 30, false);
  };

  onRightArrowClick = () => {
    let newNextScene =
      this.props.currentScene + 1 > 5 ? 0 : this.props.currentScene + 1;

    let currentModel = Object.keys(models).find(
      (key) => models[key].id === this.props.currentScene
    );
    let nextModel = Object.keys(models).find(
      (key) => models[key].id === newNextScene
    );

    // Rotate out the current model, stop its animation, then disable it
    if (currentModel) {
      let currentMesh = this.scene.getMeshByName(currentModel);
      let currentAnimation = this.scene.getAnimationGroupByName(
        `${currentModel}Animation`
      );

      if (currentMesh && currentAnimation) {
        // Rotate out the current model (180 to 360 degrees)
        this.animateModelQuaternionRotation(
          currentMesh,
          Math.PI,
          2 * Math.PI,
          0.5
        );

        // Stop the current model's animation
        // After rotation completes (0.5 seconds), disable the current model and rotate in the next model
        setTimeout(() => {
          currentMesh.setEnabled(false); // Disable the current model after rotation
          currentAnimation.reset();
          currentAnimation.stop();

          // Enable and rotate in the next model
          if (nextModel) {
            let nextMesh = this.scene.getMeshByName(nextModel);
            let nextAnimation = this.scene.getAnimationGroupByName(
              `${nextModel}Animation`
            );

            if (nextMesh) {
              nextMesh.setEnabled(true);

              // Set the next model's initial rotation to 360 degrees (off-screen)
              nextMesh.rotationQuaternion = Quaternion.RotationAxis(
                Axis.Y,
                2 * Math.PI
              );

              // Rotate the next model from 360 degrees back to 180 degrees (into view)
              this.animateModelQuaternionRotation(
                nextMesh,
                2 * Math.PI,
                Math.PI,
                0.5
              );

              // Start the next model's animation after a 200ms delay
              if (nextAnimation) {
                setTimeout(() => {
                  nextAnimation.start(true); // Play the animation with looping if needed
                }, 200);
              }
            }

            // Update camera properties
            let isMobile = window.innerWidth <= 768;
            let cameraValues = isMobile
              ? models[nextModel].mobileCameraValues
              : models[nextModel].cameraValues;

            this.camera.target = new Vector3(
              cameraValues.targetX, // Provide default 0 for X, Y, Z if not available
              cameraValues.targetY,
              cameraValues.targetZ
            );
            this.camera.alpha = cameraValues.alpha;
            this.camera.beta = cameraValues.beta;
            this.camera.radius = cameraValues.radius;
          }

          // Update the scene state after transition
          this.props.setNextScene(newNextScene);
          this.props.setCurrentScene(newNextScene);
        }, 500); // Wait for 0.5 seconds (the duration of the first model's rotation)
      }
    }
  };

  onLeftArrowClick = () => {
    let newPreviousScene =
      this.props.currentScene - 1 < 0 ? 5 : this.props.currentScene - 1;

    let currentModel = Object.keys(models).find(
      (key) => models[key].id === this.props.currentScene
    );
    let previousModel = Object.keys(models).find(
      (key) => models[key].id === newPreviousScene
    );

    // Rotate out the current model, stop its animation, then disable it
    if (currentModel) {
      let currentMesh = this.scene.getMeshByName(currentModel);
      let currentAnimation = this.scene.getAnimationGroupByName(
        `${currentModel}Animation`
      );

      if (currentMesh && currentAnimation) {
        // Rotate out the current model (180 to 0 degrees) for left rotation
        this.animateModelQuaternionRotation(currentMesh, Math.PI, 0, 0.5);

        // Stop the current model's animation
        currentAnimation.stop();

        // After rotation completes (0.5 seconds), disable the current model and rotate in the previous model
        setTimeout(() => {
          currentMesh.setEnabled(false); // Disable the current model after rotation

          // Enable and rotate in the previous model
          if (previousModel) {
            let previousMesh = this.scene.getMeshByName(previousModel);
            let previousAnimation = this.scene.getAnimationGroupByName(
              `${previousModel}Animation`
            );

            if (previousMesh) {
              previousMesh.setEnabled(true);

              // Set the previous model's initial rotation to 0 degrees (off-screen)
              previousMesh.rotationQuaternion = Quaternion.RotationAxis(
                Axis.Y,
                0
              );

              // Rotate the previous model from 0 degrees back to 180 degrees (into view)
              this.animateModelQuaternionRotation(
                previousMesh,
                0,
                Math.PI,
                0.5
              );

              // Start the previous model's animation after a 200ms delay
              if (previousAnimation) {
                setTimeout(() => {
                  previousAnimation.start(true); // Play the animation with looping if needed
                }, 200);
              }
            }

            // Update camera properties
            let isMobile = window.innerWidth <= 768;
            let cameraValues = isMobile
              ? models[previousModel].mobileCameraValues
              : models[previousModel].cameraValues;

            this.camera.target = new Vector3(
              cameraValues.targetX,
              cameraValues.targetY,
              cameraValues.targetZ
            );
            this.camera.alpha = cameraValues.alpha;
            this.camera.beta = cameraValues.beta;
            this.camera.radius = cameraValues.radius;
          }

          // Update the scene state after transition
          this.props.setNextScene(newPreviousScene);
          this.props.setCurrentScene(newPreviousScene);
        }, 500); // Wait for 0.5 seconds (the duration of the first model's rotation)
      }
    }
  };

  downloadFunction = () => {
    var disc = this.scene.getMeshByName("disc");
    let excludedNodes = [this.camera, this.engine, disc];

    let options = {
      shouldExportNode: function (node) {
        return excludedNodes.indexOf(node) === -1;
      },
    };
    GLTF2Export.GLBAsync(this.scene, "fileName", options).then((glb) => {
      const url = URL.createObjectURL(glb.glTFFiles["fileName.glb"]);
      const link = document.createElement("a");
      // link.download = 'user-info.glb'
      link.href = url;
      // link.click()
      this.props.setModelGLB(url);
      // this.props.setsceneDisplay();
    });
    setTimeout(() => {
      document
        .querySelector("model-viewer")
        .shadowRoot.querySelector("#default-ar-button")
        .click();
    }, 300);
  };

  // zoomOutFunction = () => {
  //   if (this.camera.radius < this.camera.upperRadiusLimit) {
  //     TweenMax.to(this.camera, 1, {
  //       radius: this.camera.radius + 0.1,
  //       ease: Power2.easeOut,
  //     });
  //   }
  // };

  // zoomInFunction = () => {
  //   if (this.camera.radius > this.camera.lowerRadiusLimit) {
  //     TweenMax.to(this.camera, 1, {
  //       radius: this.camera.radius - 0.1,
  //       ease: Power2.easeOut,
  //     });
  //   }
  // };

  // stopRotationFunction = () => {
  //   if (this.rotationSpeed.current != 0) {
  //     this.rotationSpeed.current = 0;
  //   } else {
  //     this.rotationSpeed.current = -0.002;
  //   }
  // };
  // fullScreenFunction = () => {
  //   if (!document.fullscreenElement) {
  //     this.stage.requestFullscreen();
  //   } else {
  //     document.exitFullscreen();
  //   }
  // };

  //Build the scene when the component has been loaded.

  componentDidMount() {
    this.setEngine();
    this.setScene();
    this.setCamera(); // Sets the camera initially

    // Load models and enable the first model (Cam) when ready
    this.loadModels();

    window.addEventListener("resize", this.onResizeWindow);
    window.addEventListener("previous-model", this.onLeftArrowClick);
    window.addEventListener("next-model", this.onRightArrowClick);
  }

  //Renderes our Canvas tag and saves a reference to it.
  render() {
    return (
      <>
        <canvas
          className="scene unselectable"
          ref={(el) => (this.stage = el)}
        ></canvas>
        {/* {/(iphone|ipod|ipad|android|blackberry|windows ce|palm|symbian)/i.test(
          window.navigator.userAgent
        ) ? (
          <button
            className="Download"
            onClick={() => this.downloadFunction()}
            style={{
              position: "absolute",
              top: "2%",
              left: "1%",
              border: "0",
              opacity: "0.4",
              backgroundColor: "transparent",
            }}
          >
            <img src="ar.svg" style={{ height: "40px", width: "40px" }} />
          </button>
        ) : null} */}
      </>
    );
  }
}
//returns the scene to be used by other components
export default Scene3d;
